import { Box, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useCallback, useMemo, useRef } from "react";
import type { ScrapMixStatistic } from "../models";
import { formatCompositionTooltip } from "../utils/compositionTooltip";
import { formatWeight } from "../utils/formatWeight";
import { computeTooltipPosition } from "../utils/tooltipPosition";

interface Props {
  statistics: ScrapMixStatistic[];
  expandedRecipes: ReadonlySet<number>;
  onToggleRecipe: (recipeFamilyId: number) => void;
}

interface ChartRow {
  id: string;
  level: "recipe" | "charge";
  recipeFamilyId: number;
  recipeName: string;
  chargeNumber: number | null;
}

interface ChartClickEvent {
  componentType?: string;
  value?: unknown;
  name?: string;
  data?: { rowId?: string };
}

const FALLBACK_COLOR = "#607d8b";
const RECIPE_BAR_HEIGHT = 14;
const CHARGE_BAR_HEIGHT = 8;

const statisticKey = (
  level: "recipe" | "charge",
  recipeFamilyId: number,
  chargeNumber: number | null,
  commodityId: number | null,
) => `${level}-${recipeFamilyId}-${chargeNumber ?? "all"}-${commodityId}`;

export const CompositionChart = ({
  statistics,
  expandedRecipes,
  onToggleRecipe,
}: Props) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);

  const chartModel = useMemo(() => {
    const placeTooltip = (
      point: [number, number],
      _params: unknown,
      _dom: unknown,
      _rect: unknown,
      size: { contentSize: [number, number] },
    ) => {
      const bounds = chartContainerRef.current?.getBoundingClientRect();
      return computeTooltipPosition(
        point,
        size.contentSize,
        bounds?.left ?? 0,
        bounds?.top ?? 0,
      );
    };

    const nonTotalStatistics = statistics.filter(
      (statistic) => !statistic.IsTotal,
    );
    const recipes = Array.from(
      new Map(
        statistics
          .filter((statistic) => statistic.Level === "recipe")
          .map((statistic) => [statistic.RecipeFamilyId, statistic.RecipeName]),
      ),
    ).sort((left, right) => left[1].localeCompare(right[1]));
    const materials = Array.from(
      new Map(
        nonTotalStatistics.map((statistic) => [
          statistic.CommodityId,
          {
            id: statistic.CommodityId,
            name: statistic.CommodityName,
            color: statistic.CommodityColor || FALLBACK_COLOR,
          },
        ]),
      ).values(),
    ).sort((left, right) => left.name.localeCompare(right.name));

    const chargeNumbersByRecipe = new Map<number, Set<number>>();
    for (const statistic of statistics) {
      if (statistic.Level !== "charge" || statistic.ChargeNumber === null)
        continue;
      const chargeNumbers =
        chargeNumbersByRecipe.get(statistic.RecipeFamilyId) ??
        new Set<number>();
      chargeNumbers.add(statistic.ChargeNumber);
      chargeNumbersByRecipe.set(statistic.RecipeFamilyId, chargeNumbers);
    }

    const visualRows: ChartRow[] = recipes.flatMap(
      ([recipeFamilyId, recipeName]) => {
        const recipeRow: ChartRow = {
          id: `recipe-${recipeFamilyId}`,
          level: "recipe",
          recipeFamilyId,
          recipeName,
          chargeNumber: null,
        };
        if (!expandedRecipes.has(recipeFamilyId)) return [recipeRow];

        const chargeRows = Array.from(
          chargeNumbersByRecipe.get(recipeFamilyId) ?? [],
        )
          .sort((left, right) => left - right)
          .map<ChartRow>((chargeNumber) => ({
            id: `recipe-${recipeFamilyId}-charge-${chargeNumber}`,
            level: "charge",
            recipeFamilyId,
            recipeName,
            chargeNumber,
          }));
        return [recipeRow, ...chargeRows];
      },
    );

    // ECharts renders the first horizontal category at the bottom, so reverse
    // the desired visual order to keep each recipe above Charges 1/2/3.
    const chartRows = [...visualRows].reverse();
    const rowsById = new Map(chartRows.map((row) => [row.id, row]));
    const values = new Map(
      nonTotalStatistics.map((statistic) => [
        statisticKey(
          statistic.Level,
          statistic.RecipeFamilyId,
          statistic.ChargeNumber,
          statistic.CommodityId,
        ),
        statistic.AverageWeight,
      ]),
    );

    const dataFor = (materialId: number | null, level: "recipe" | "charge") =>
      chartRows.map((row) =>
        row.level === level
          ? {
              value: Math.round(
                values.get(
                  statisticKey(
                    row.level,
                    row.recipeFamilyId,
                    row.chargeNumber,
                    materialId,
                  ),
                ) ?? 0,
              ),
              rowId: row.id,
            }
          : null,
      );

    const option = {
      animationDuration: 250,
      grid: { top: 10, right: 42, bottom: 48, left: 8, containLabel: true },
      legend: {
        type: "scroll",
        bottom: 0,
        data: materials.map((material) => material.name),
      },
      tooltip: {
        trigger: "axis",
        appendTo: "body",
        position: placeTooltip,
        axisPointer: { type: "shadow" },
        formatter: (params: Parameters<typeof formatCompositionTooltip>[0]) =>
          formatCompositionTooltip(params, rowsById),
      },
      xAxis: {
        type: "value",
        axisLabel: {
          formatter: formatWeight,
        },
      },
      yAxis: {
        type: "category",
        data: chartRows.map((row) => row.id),
        axisLabel: {
          width: 108,
          overflow: "truncate",
          formatter: (rowId: string) => {
            const row = rowsById.get(rowId);
            if (!row) return rowId;
            if (row.level === "charge")
              return `{charge|\u21b3 Ch. ${row.chargeNumber}}`;
            return `{recipe|${expandedRecipes.has(row.recipeFamilyId) ? "\u25be" : "\u25b8"} ${row.recipeName}}`;
          },
          rich: {
            recipe: {
              color: "#333333",
              fontSize: 12,
              fontWeight: 700,
            },
            charge: {
              color: "#757575",
              fontSize: 11,
              padding: [0, 0, 0, 14],
            },
          },
        },
        triggerEvent: true,
      },
      series: materials.flatMap((material) => [
        {
          name: material.name,
          type: "bar",
          stack: "recipe-composition",
          barWidth: RECIPE_BAR_HEIGHT,
          itemStyle: { color: material.color },
          emphasis: { focus: "series" },
          data: dataFor(material.id, "recipe"),
        },
        {
          name: material.name,
          type: "bar",
          stack: "scrap-mix",
          barWidth: CHARGE_BAR_HEIGHT,
          barGap: "-100%",
          itemStyle: { color: material.color },
          emphasis: { focus: "series" },
          data: dataFor(material.id, "charge"),
        },
      ]),
    };

    return { chartRows, rowsById, option };
  }, [expandedRecipes, statistics]);

  const handleChartClick = useCallback(
    (event: ChartClickEvent) => {
      const rowId =
        event.data?.rowId ??
        (event.componentType === "yAxis" && typeof event.value === "string"
          ? event.value
          : event.name);
      if (!rowId) return;
      const row = chartModel.rowsById.get(rowId);
      if (row?.level === "recipe") onToggleRecipe(row.recipeFamilyId);
    },
    [chartModel.rowsById, onToggleRecipe],
  );

  const chartEvents = useMemo(
    () => ({ click: handleChartClick }),
    [handleChartClick],
  );

  if (chartModel.chartRows.length === 0) {
    return (
      <Box
        sx={{ minHeight: 180, display: "grid", placeItems: "center" }}
        data-testid="composition-chart-empty"
      >
        <Typography color="text.secondary">
          No scrap mix data matches the selected filters.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={chartContainerRef}
      data-testid="composition-chart"
      data-expanded-recipes={expandedRecipes.size}
    >
      <ReactECharts
        option={chartModel.option}
        onEvents={chartEvents}
        notMerge
        style={{
          height: Math.max(190, chartModel.chartRows.length * 24 + 100),
          width: "100%",
        }}
      />
    </Box>
  );
};
